import {
  IAccount,
  IAccountService,
  ProductType,
  ServiceType,
} from "@origin-digital/event-dispatcher";
import { mapServiceType } from "./mapServiceType";
import { BackendCrm } from "@origin-digital/platform-enums";

interface ServiceConfig {
  rank: number;
  accountMapper: (account: IAccount) => IAccountService[];
}

export const isClosedAccount = (account: IAccount): boolean =>
  account.status === "CLOSED" || account.status === "CANCELLED";

/**
 * Return an IAccountService for each service on the account.
 */
const defaultAccountMapper = (account: IAccount): IAccountService[] =>
  account.services.map((service) => ({
    accountId: account.backendAccountId,
    serviceId: service.backendServiceId,
    backendType: account.backend,
    serviceType: mapServiceType(account),
    productType: service.productType,
    isClosed: isClosedAccount(account),
  }));

/**
 * Configuration for each service selection type.
 * Includes the sorting rank and a mapper for transforming the account into one or more service selection objects.
 */
const SERVICE_TYPE_CONFIGURATION: Record<ServiceType, ServiceConfig> = {
  ELECTRICITY: {
    rank: 1,
    /**
     * There can be multiple electricity services
     */
    accountMapper: defaultAccountMapper,
  },
  NATURAL_GAS: {
    rank: 2,
    /**
     * There can be multiple gas services
     */
    accountMapper: defaultAccountMapper,
  },
  /**
   *
   */
  HOT_WATER: {
    rank: 3,
    /**
     * Get the hot water service on the account, and ignore the others
     */
    accountMapper: (account) => {
      const hotWaterService = account.services.find(
        (service) => service.productType === ProductType.HOT_WATER
      );
      return [
        {
          accountId: account.backendAccountId,
          serviceId: hotWaterService?.backendServiceId ?? "",
          backendType: account.backend,
          serviceType: ServiceType.HOT_WATER,
          productType: hotWaterService?.productType ?? ProductType.UNKNOWN,
          isClosed: isClosedAccount(account),
        },
      ];
    },
  },
  BROADBAND: {
    rank: 4,
    /**
     * Get one service for the SUPERLOOP account and ignore the old BROADBAND account type
     */
    accountMapper: (account) =>
      account.backend === BackendCrm.SUPERLOOP
        ? [
            {
              accountId: account.backendAccountId,
              serviceId: account.services?.[0].backendServiceId ?? "",
              backendType: account.backend,
              serviceType: ServiceType.BROADBAND,
              productType:
                account.services?.[0].productType ?? ProductType.UNKNOWN,
              isClosed: isClosedAccount(account),
            },
          ]
        : [],
  },
  LPG: {
    rank: 5,
    /**
     * Return a single account-service for one account (one LPG account has multiple services with multiple properties)
     */
    accountMapper: (account) => [
      {
        accountId: account.backendAccountId,
        serviceId: "",
        backendType: account.backend,
        serviceType: ServiceType.LPG,
        productType: ProductType.UNKNOWN,
        isClosed: isClosedAccount(account),
      },
    ],
  },
  /**
   * Include ALL accounts-services even if not displayed to user, they're required for account filtering by property
   */
  OHM: { rank: 6, accountMapper: defaultAccountMapper },
  VPP_BATTERY: { rank: 7, accountMapper: defaultAccountMapper },
  UNKNOWN: { rank: 8, accountMapper: defaultAccountMapper },
};

const sortAccountServices = (a: IAccountService, b: IAccountService) => {
  if (
    SERVICE_TYPE_CONFIGURATION[a.serviceType].rank !==
    SERVICE_TYPE_CONFIGURATION[b.serviceType].rank
  ) {
    return (
      (SERVICE_TYPE_CONFIGURATION[a.serviceType].rank ?? Number.MAX_VALUE) -
      (SERVICE_TYPE_CONFIGURATION[b.serviceType].rank ?? Number.MAX_VALUE)
    );
  }
  if (a.isClosed !== b.isClosed) {
    return a.isClosed ? 1 : -1;
  }
  return a.accountId.localeCompare(b.accountId);
};

export const mapToAccountServices = (
  accounts: IAccount[]
): IAccountService[] => {
  const result: IAccountService[] = [];
  accounts.forEach((account) => {
    const serviceType = mapServiceType(account);
    result.push(
      ...SERVICE_TYPE_CONFIGURATION[serviceType].accountMapper(account)
    );
  });
  return result.sort(sortAccountServices);
};
